/**
 * AppConfigEditor — schema-driven configuration UI.
 *
 * Reads `manifest.config_options` + `manifest.feature_flags` (unified into a
 * single schema by the backend) and auto-renders the appropriate form
 * control per field. No per-app code lives here — every future COS
 * Application gets a settings page for free by declaring its schema.
 *
 * Supported field types (must match core/config_schema.py):
 *   string · text · email · url · int · float · bool
 *   select · multiselect · password · color · list
 *
 * Grouping: fields with the same `group` label are rendered under one
 * subsection. Fields without a group fall into "General".
 */
import React, { useMemo, useState, useEffect } from "react";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Save, RotateCcw, AlertCircle } from "lucide-react";

/* ─────────────────────────────────────────────────────────────────────
   Field renderers
   ───────────────────────────────────────────────────────────────────── */
function FieldControl({ field, value, onChange }) {
  const tid = `cfg-field-${field.key}`;
  const t = field.type;

  if (t === "bool") {
    return (
      <div className="flex items-center gap-3 py-1">
        <Switch checked={!!value} onCheckedChange={onChange}
                data-testid={tid} id={tid}/>
        <Label htmlFor={tid} className="text-sm text-slate-700 cursor-pointer">
          {value ? "Enabled" : "Disabled"}
        </Label>
      </div>
    );
  }

  if (t === "text") {
    return (
      <Textarea
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={field.placeholder || ""}
        rows={3}
        data-testid={tid}
      />
    );
  }

  if (t === "select") {
    const options = (field.options || []).map(o =>
      typeof o === "string" ? { value: o, label: o } : o);
    return (
      <Select value={value ?? ""} onValueChange={onChange}>
        <SelectTrigger data-testid={tid}>
          <SelectValue placeholder={field.placeholder || "Choose…"}/>
        </SelectTrigger>
        <SelectContent>
          {options.map(o => (
            <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  if (t === "multiselect") {
    const options = (field.options || []).map(o =>
      typeof o === "string" ? { value: o, label: o } : o);
    const selected = Array.isArray(value) ? value : [];
    const toggle = (v) => {
      const next = selected.includes(v)
        ? selected.filter(x => x !== v)
        : [...selected, v];
      onChange(next);
    };
    return (
      <div className="flex flex-wrap gap-1.5" data-testid={tid}>
        {options.map(o => {
          const active = selected.includes(o.value);
          return (
            <button
              key={o.value}
              type="button"
              onClick={() => toggle(o.value)}
              data-testid={`${tid}-opt-${o.value}`}
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                active
                  ? "text-white border-transparent"
                  : "text-slate-600 border-slate-300 hover:border-slate-500"
              }`}
              style={active ? { background: "var(--cod-navy)" } : {}}
            >
              {o.label}
            </button>
          );
        })}
      </div>
    );
  }

  if (t === "list") {
    // Store as list, display as comma-separated string
    const display = Array.isArray(value) ? value.join(", ") : (value ?? "");
    return (
      <Input
        value={display}
        onChange={(e) => onChange(
          e.target.value.split(",").map(x => x.trim()).filter(Boolean)
        )}
        placeholder={field.placeholder || "value1, value2, value3"}
        data-testid={tid}
      />
    );
  }

  if (t === "int" || t === "float") {
    return (
      <Input
        type="number"
        step={t === "float" ? "any" : "1"}
        min={field.min}
        max={field.max}
        value={value ?? ""}
        onChange={(e) => {
          const raw = e.target.value;
          if (raw === "") { onChange(null); return; }
          onChange(t === "int" ? parseInt(raw, 10) : parseFloat(raw));
        }}
        placeholder={field.placeholder || ""}
        data-testid={tid}
      />
    );
  }

  if (t === "password") {
    return (
      <Input
        type="password"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder="••••••"
        data-testid={tid}
      />
    );
  }

  if (t === "color") {
    return (
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={value || "#000000"}
          onChange={(e) => onChange(e.target.value)}
          className="w-10 h-10 rounded cursor-pointer border border-slate-300"
          data-testid={tid}
        />
        <Input
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="#000000"
          className="max-w-[140px] font-mono"
        />
      </div>
    );
  }

  // Default: string / email / url
  return (
    <Input
      type={t === "email" ? "email" : t === "url" ? "url" : "text"}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={field.placeholder || ""}
      data-testid={tid}
    />
  );
}

/* ─────────────────────────────────────────────────────────────────────
   Main editor
   ───────────────────────────────────────────────────────────────────── */
export function AppConfigEditor({ schema, effective, onSave, saving }) {
  const [values, setValues] = useState({});
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState(null);

  // Initialize / reset when the effective config changes (e.g., after save).
  useEffect(() => {
    setValues(effective || {});
    setDirty(false);
    setError(null);
  }, [effective]);

  const groups = useMemo(() => {
    const out = {};
    for (const f of schema || []) {
      const g = f.group || "General";
      if (!out[g]) out[g] = [];
      out[g].push(f);
    }
    // Preserve declared order within a group
    for (const g of Object.keys(out)) {
      out[g].sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
    }
    return out;
  }, [schema]);

  const setField = (k, v) => {
    setValues(prev => ({ ...prev, [k]: v }));
    setDirty(true);
    setError(null);
  };

  const reset = () => {
    setValues(effective || {});
    setDirty(false);
    setError(null);
  };

  const save = async () => {
    try {
      // Only send changed keys
      const changed = {};
      for (const k of Object.keys(values)) {
        if (JSON.stringify(values[k]) !== JSON.stringify(effective?.[k])) {
          changed[k] = values[k];
        }
      }
      if (Object.keys(changed).length === 0) { setDirty(false); return; }
      await onSave(changed);
      setDirty(false);
    } catch (e) {
      setError(e?.response?.data?.detail || "Save failed");
    }
  };

  if (!schema || schema.length === 0) {
    return (
      <div className="text-sm text-slate-500 italic py-6 text-center border border-dashed border-slate-200 rounded-lg">
        This application does not expose any configuration.
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="cos-config-editor">
      {error && (
        <div className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">
          <AlertCircle size={16} className="mt-0.5 shrink-0"/>
          <span>{error}</span>
        </div>
      )}

      {Object.entries(groups).map(([groupName, fields]) => (
        <div key={groupName} data-testid={`cos-config-group-${groupName.toLowerCase().replace(/\s+/g,"-")}`}>
          <div className="cod-eyebrow mb-3" style={{ color: "var(--cod-navy)" }}>
            {groupName}
          </div>
          <div className="space-y-4 border border-slate-200 rounded-lg p-4 bg-white">
            {fields.map(f => (
              <div key={f.key} className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="md:col-span-1">
                  <Label className="text-sm text-slate-800 font-semibold flex items-center gap-1.5">
                    {f.label || f.key}
                    {f.required && <span className="text-red-600">*</span>}
                    {f.secret && <Badge variant="outline" className="text-[9px]">secret</Badge>}
                  </Label>
                  {f.description && (
                    <p className="text-xs text-slate-500 mt-1 leading-snug">
                      {f.description}
                    </p>
                  )}
                  <div className="text-[10px] text-slate-400 font-mono mt-1">{f.key}</div>
                </div>
                <div className="md:col-span-2">
                  <FieldControl
                    field={f}
                    value={values[f.key]}
                    onChange={(v) => setField(f.key, v)}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      <div className="flex items-center justify-end gap-2 border-t border-slate-200 pt-4">
        {dirty && (
          <span className="text-xs text-amber-700 mr-auto flex items-center gap-1">
            <AlertCircle size={12}/> Unsaved changes
          </span>
        )}
        <Button variant="ghost" size="sm" onClick={reset} disabled={!dirty || saving}
                data-testid="cos-config-reset">
          <RotateCcw size={14} className="mr-1"/> Reset
        </Button>
        <Button className="cod-bg-red" size="sm" onClick={save} disabled={!dirty || saving}
                data-testid="cos-config-save">
          <Save size={14} className="mr-1"/> {saving ? "Saving…" : "Save changes"}
        </Button>
      </div>
    </div>
  );
}
